import URI from 'jsuri';

import API from 'js/lib/api';
import user from 'js/lib/user';

import { QuestionTypes, ResponseTypes } from 'bundles/survey-form/constants/SurveyFormConstants';
import type { QuestionType } from 'bundles/survey-form/constants/SurveyFormConstants';

import { ONBOARDING_SURVEY_BASE_ID, ONBOARDING_SURVEY_VERSION, QUESTION_RESPONSE_TYPE_MAP } from '../data/constants';
import type {
  Answer,
  NonTerminalSurveyData,
  QuestionAnswer,
  SurveyCreateRequest,
  SurveyData,
  SurveyQuestion,
  SurveyRequestAnswers,
  SurveyResponse,
  SurveyResponseQuestion,
  SurveyUpdateRequest,
  SurveyUpdateRequestResponses,
} from '../types/Survey';

const onboardingSurveyResponsesAPI = API('/api/surveySectionResponses.v1', { type: 'rest' });

export const formatQuestions = (questions: SurveyResponseQuestion[]): SurveyQuestion[] => {
  return questions.map((question) => {
    const questionType = Object.keys(question.content)[0] as QuestionType;
    const content = question.content[questionType];
    // short answer is freeform - disregard the BE response for the options in this case
    const options = questionType === QuestionTypes.SHORT_ANSWER ? [] : content.options;
    return {
      type: questionType,
      prompt: content.prompt,
      options,
      id: question.id,
      configurations: question.configurations,
      isRequired: question.isRequired,
    };
  });
};

export const formatSurveyResponse = (response: SurveyResponse): SurveyData => {
  if (!response || response.elements.length === 0) {
    throw new Error('No survey response available');
  }

  const data = response.elements[0];
  const { responseId, sectionId, section } = data;
  const { isTerminal, title } = section;

  if (isTerminal) {
    return {
      responseId,
      sectionId,
      isTerminal,
    };
  }

  return {
    responseId,
    sectionId,
    isTerminal,
    title,
    questions: formatQuestions(section.questions),
  };
};

export const createSurvey = (): Promise<SurveyData> => {
  const payload: SurveyCreateRequest = {
    surveyId: {
      baseId: ONBOARDING_SURVEY_BASE_ID,
      version: ONBOARDING_SURVEY_VERSION,
    },
    scope: {
      identity: {
        'org.coursera.survey.scope.LoggedInIdentity': {
          userId: user.get()?.id,
        },
      },
      context: {
        'org.coursera.survey.scope.NoContext': {},
      },
    },
  };
  return new Promise((resolve, reject) => {
    const uri = new URI().addQueryParam('fields', 'section,sectionId');
    Promise.resolve(onboardingSurveyResponsesAPI.post(uri.toString(), { data: payload }))
      .then(formatSurveyResponse)
      .then(resolve)
      .catch(reject);
  });
};

const formatQuestionResponse = (
  responseType: (typeof ResponseTypes)[keyof typeof ResponseTypes],
  answer?: Answer,
  questionId?: string
): QuestionAnswer | null => {
  switch (responseType) {
    case ResponseTypes.MULTIPLE_CHOICE_RESPONSE:
      return answer ? { id: answer } : null;
    case ResponseTypes.CHECKBOX_RESPONSE:
      if (questionId === 'roleSelection') return null;
      return answer?.length ? { ids: answer } : null;
    case ResponseTypes.SHORT_ANSWER_RESPONSE:
      return answer ? { text: answer } : null;
    case ResponseTypes.DROPDOWN_RESPONSE:
      return answer ? { id: answer } : null;
    case ResponseTypes.TYPEAHEAD_RESPONSE:
      if (typeof answer === 'object' && !Array.isArray(answer)) {
        const typeaheadKey = Object.keys(answer)[0];
        return { [typeaheadKey]: answer[typeaheadKey] };
      } else {
        return null;
      }
    default:
      throw new Error(`Cannot format unsupported question response type: ${responseType}`);
  }
};

const formatResponses = (questions: SurveyQuestion[], sectionId: string, answers?: SurveyRequestAnswers) => {
  return questions.reduce((formattedResponses, currentQuestion) => {
    const responseType = QUESTION_RESPONSE_TYPE_MAP[currentQuestion.type];
    const formattedResponse = formatQuestionResponse(
      responseType,
      answers?.[sectionId]?.[currentQuestion.id],
      currentQuestion.id
    );
    if (formattedResponse) {
      // eslint-disable-next-line no-param-reassign
      formattedResponses[currentQuestion.id] = {
        [responseType]: formattedResponse,
      };
    }
    return formattedResponses;
  }, {} as SurveyUpdateRequestResponses);
};

export const updateSurvey = ({
  responseId,
  sectionId,
  questions,
  answers,
}: Pick<NonTerminalSurveyData, 'responseId' | 'sectionId' | 'questions'> & {
  answers?: SurveyRequestAnswers;
}): Promise<SurveyData> => {
  const payload: SurveyUpdateRequest = {
    sectionId,
    responses: formatResponses(questions, sectionId, answers),
  };

  return new Promise((resolve, reject) => {
    const uri = new URI(responseId).addQueryParam('fields', 'section,sectionId');
    Promise.resolve(onboardingSurveyResponsesAPI.put(uri.toString(), { data: payload }))
      .then(formatSurveyResponse)
      .then(resolve)
      .catch(reject);
  });
};
